/**
 * Shell-owned employee chat (DM): pick a coworker, open the direct message
 * with them, chat in a floating panel. The panel only opens and closes.
 * What the other person writes arrives live over the shell socket's `chat`
 * channel (`handleLiveEvent`, wired by the panel).
 */

import { computed, ref, shallowRef } from 'vue';
import { readShellAccount, readShellUser } from '@blpde/connect';
import {
  listChatAccounts,
  listMessages,
  openDirectChannel,
  sendMessage,
  type ChatAccount,
  type ChatChannel,
  type ChatMessage,
} from './chatApi';

export type ShellChatView = 'picker' | 'thread';

/** A frame of the chat module on the shell socket. */
export interface ShellChatLiveEvent {
  type: string;
  channelId?: number;
  payload?: unknown;
}

/** Open the floating panel, optionally straight into a DM with someone. */
export type ShellChatOpenOptions = {
  accountId?: number | null;
  name?: string | null;
  email?: string | null;
};

const open = ref(false);
const view = ref<ShellChatView>('picker');
const booting = ref(false);
const loadingThread = ref(false);
const sending = ref(false);
const error = ref<string | null>(null);
const accounts = ref<ChatAccount[]>([]);
const myAccountId = ref<number | null>(null);
const partner = shallowRef<ChatAccount | null>(null);
const channel = shallowRef<ChatChannel | null>(null);
const messages = ref<ChatMessage[]>([]);
const draft = ref('');
const search = ref('');
/**
 * The frames that arrived while the open conversation loaded: replayed onto
 * the loaded page, so a message written meanwhile is not lost behind it.
 */
let framesWhileLoading: ShellChatLiveEvent[] = [];
/**
 * Text that failed to send after its conversation was left, by channel: it
 * goes back into that conversation's box when it is opened again — never
 * into the box of the conversation open now.
 */
const unsentByChannel = new Map<number, string>();

/**
 * The conversation in the server's order: confirmed messages by id, the
 * optimistic ones (negative ids, not answered yet) after them. HTTP answers
 * and socket frames arrive in any order; the list never shows that order.
 */
function inServerOrder(list: ChatMessage[]): ChatMessage[] {
  return [...list].sort((a, b) => {
    const aPending = a.id < 0;
    const bPending = b.id < 0;
    if (aPending !== bPending) return aPending ? 1 : -1;
    return aPending ? b.id - a.id : a.id - b.id;
  });
}

export function useShellChat() {
  const stickKey = computed(() => {
    const last = messages.value.at(-1);
    return `${messages.value.length}:${last?.id ?? 0}:${last?.content?.length ?? 0}`;
  });

  const filteredAccounts = computed(() => {
    const me = readShellAccount();
    const myUuid = me?.uuid;
    const myEmail = me?.email || readShellUser()?.email;
    const q = search.value.toLowerCase().trim();
    return accounts.value
      .filter((a) => a.active !== false)
      .filter((a) => a.uuid !== myUuid && a.email !== myEmail)
      .filter((a) => {
        if (myAccountId.value != null && a.id === myAccountId.value) return false;
        return true;
      })
      .filter((a) => {
        if (!q) return true;
        return (
          a.name?.toLowerCase().includes(q) ||
          a.email?.toLowerCase().includes(q)
        );
      })
      .slice()
      .sort((a, b) =>
        (a.name || a.email || '').localeCompare(b.name || b.email || '', 'de', {
          sensitivity: 'base',
        }),
      );
  });

  async function resolveMyAccountId(list: ChatAccount[]): Promise<number | null> {
    const account = readShellAccount();
    const user = readShellUser();
    const uuid = account?.uuid;
    const email = account?.email || user?.email;
    const name = account?.name || user?.name;
    const hit: ChatAccount | undefined =
      (uuid ? list.find((a) => a.uuid === uuid) : undefined) ??
      (email ? list.find((a) => a.email === email) : undefined) ??
      (name ? list.find((a) => a.name === name) : undefined);
    return hit?.id ?? null;
  }

  async function openChat(opts?: ShellChatOpenOptions) {
    const targetId =
      opts?.accountId != null && opts.accountId > 0 ? opts.accountId : null;

    open.value = true;
    error.value = null;
    // Bare open starts at picker so user can switch coworker. When a target
    // account is provided (AccountChip "Nachricht senden"), we jump into that
    // DM after boot instead of routing to the full /chat app.
    view.value = 'picker';
    partner.value = null;
    channel.value = null;
    messages.value = [];
    draft.value = '';
    search.value = '';
    booting.value = true;

    try {
      const accs = await listChatAccounts();
      accounts.value = accs;
      myAccountId.value = await resolveMyAccountId(accs);
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'error';
    } finally {
      booting.value = false;
    }

    if (targetId != null && !error.value) {
      const hit =
        accounts.value.find((a) => a.id === targetId) ??
        ({
          id: targetId,
          uuid: '',
          name: (opts?.name ?? opts?.email ?? '').trim() || String(targetId),
          email: (opts?.email ?? '').trim(),
          active: true,
        } satisfies ChatAccount);
      await selectPartner(hit);
    }
  }

  async function selectPartner(account: ChatAccount) {
    if (loadingThread.value || booting.value) return;
    error.value = null;
    loadingThread.value = true;
    partner.value = account;
    view.value = 'thread';
    messages.value = [];
    draft.value = '';

    try {
      let myId = myAccountId.value;
      if (myId == null) {
        if (accounts.value.length === 0) {
          accounts.value = await listChatAccounts();
        }
        myId = await resolveMyAccountId(accounts.value);
        myAccountId.value = myId;
      }
      if (myId == null) {
        throw new Error('no-self');
      }

      // One direct message per pair of accounts: the server answers the
      // existing channel or a new one.
      const ch = await openDirectChannel(account.id);
      framesWhileLoading = [];
      channel.value = ch;
      const msgs = await listMessages(ch.id);
      const nameById = new Map(accounts.value.map((a) => [a.id, a.name || a.email]));
      if (myId) nameById.set(myId, readShellAccount()?.name || readShellUser()?.name || 'Du');
      messages.value = msgs.map((m) => {
        const sid = m.senderAccountId ?? m.sender_account_id;
        return {
          ...m,
          senderName: sid != null ? nameById.get(sid) : undefined,
        };
      });
      const missed = framesWhileLoading;
      framesWhileLoading = [];
      loadingThread.value = false;
      for (const frame of missed) handleLiveEvent(frame);
      const unsent = unsentByChannel.get(ch.id);
      if (unsent && !draft.value.trim()) draft.value = unsent;
      unsentByChannel.delete(ch.id);
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'error';
      // Stay on thread with error so user sees it; allow back to picker.
    } finally {
      loadingThread.value = false;
    }
  }

  function backToPicker() {
    view.value = 'picker';
    partner.value = null;
    channel.value = null;
    messages.value = [];
    draft.value = '';
    error.value = null;
  }

  async function send(content: string) {
    const text = content.trim();
    const ch = channel.value;
    const myId = myAccountId.value;
    if (!text || !ch || myId == null || sending.value) return;

    draft.value = '';
    sending.value = true;
    error.value = null;
    const optimistic: ChatMessage = {
      id: -Date.now(),
      channelId: ch.id,
      senderAccountId: myId,
      content: text,
      createdAt: new Date().toISOString(),
      senderName: readShellAccount()?.name || readShellUser()?.name || 'Du',
    };
    messages.value = [...messages.value, optimistic];

    // The conversation may change while the request runs: its answer only
    // touches the conversation it was sent in.
    const stillOpen = () => channel.value?.id === ch.id;
    try {
      const saved = await sendMessage(ch.id, text, myId);
      if (!stillOpen()) return;
      // The live frame may have brought the message first: keep that one.
      const arrived = messages.value.some((m) => m.id === saved.id);
      messages.value = inServerOrder(
        arrived
          ? messages.value.filter((m) => m.id !== optimistic.id)
          : messages.value.map((m) =>
              m.id === optimistic.id
                ? {
                    ...saved,
                    senderName: optimistic.senderName,
                  }
                : m,
            ),
      );
    } catch (e: unknown) {
      if (!stillOpen()) {
        // Kept for its own conversation, restored when that one opens.
        unsentByChannel.set(ch.id, text);
        return;
      }
      messages.value = messages.value.filter((m) => m.id !== optimistic.id);
      error.value = e instanceof Error ? e.message : 'error';
      // What was not sent goes back into the composer, unless something new
      // was typed meanwhile.
      if (!draft.value.trim()) draft.value = text;
    } finally {
      sending.value = false;
    }
  }

  /** The name shown for a sender: the account list, or the shell's own name. */
  function senderNameOf(senderId: number | undefined): string | undefined {
    if (senderId == null) return undefined;
    if (senderId === myAccountId.value) {
      return readShellAccount()?.name || readShellUser()?.name || 'Du';
    }
    const account = accounts.value.find((a) => a.id === senderId);
    return account ? account.name || account.email : undefined;
  }

  /**
   * A frame of the chat module: a new, changed or deleted message of the open
   * direct message. Anything else, or another channel, is not the panel's.
   */
  function handleLiveEvent(raw: unknown) {
    const event = raw as ShellChatLiveEvent | null;
    const ch = channel.value;
    if (!event || typeof event.type !== 'string' || !ch) return;
    if (event.channelId !== ch.id) return;
    if (loadingThread.value) {
      framesWhileLoading.push(event);
      return;
    }
    switch (event.type) {
      case 'new_message': {
        const msg = event.payload as ChatMessage | undefined;
        if (!msg?.id || messages.value.some((m) => m.id === msg.id)) return;
        const senderId = msg.senderAccountId ?? msg.sender_account_id;
        messages.value = inServerOrder([
          ...messages.value,
          { ...msg, senderName: senderNameOf(senderId) },
        ]);
        break;
      }
      case 'message_updated': {
        const msg = event.payload as ChatMessage | undefined;
        if (!msg?.id) return;
        messages.value = messages.value.map((m) =>
          m.id === msg.id ? { ...m, ...msg, senderName: m.senderName } : m,
        );
        break;
      }
      case 'message_deleted': {
        const id = Number(event.payload);
        messages.value = messages.value.filter((m) => m.id !== id);
        break;
      }
    }
  }

  function closeChat() {
    open.value = false;
    view.value = 'picker';
    partner.value = null;
    channel.value = null;
    messages.value = [];
    draft.value = '';
    search.value = '';
    error.value = null;
    sending.value = false;
  }

  return {
    open,
    view,
    booting,
    loadingThread,
    sending,
    error,
    accounts,
    filteredAccounts,
    myAccountId,
    partner,
    channel,
    messages,
    draft,
    search,
    stickKey,
    openChat,
    closeChat,
    selectPartner,
    backToPicker,
    send,
    handleLiveEvent,
  };
}
